import { Alert, Table } from 'antd'
import dayjs from 'dayjs'
import { MdStar } from 'react-icons/md'

import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { cx } from '@/Ship/Helpers/cssClass'

import { AssignmentResource } from '@/Containers/AssignmentSection/AssignmentContainer/Resources/AssignmentResource'

import type { CompletionAssignment, CompletionGroupBy } from '../Contracts/Report'
import { useReportFetch } from '../Hooks/useReportFetch'

import styles from './Report.module.css'

/** Ліміт деталізації на бекенді (CompletionReportAction::ASSIGNMENTS_LIMIT) */
const LIMIT = 500

const formatNumber = (value: number | null, digits = 0): string =>
    value === null ? '—' : value.toLocaleString('uk-UA', { maximumFractionDigits: digits })

interface CompletionAssignmentsProps {
    groupBy: CompletionGroupBy
    groupKey: string
    params: Record<string, string>
}

/** Задачі групи (розгорнутий рядок «Виконання задач»); номер відкриває картку задачі */
export const CompletionAssignments = ({ groupBy, groupKey, params }: CompletionAssignmentsProps) => {
    const { data, loading, error } = useReportFetch<{ items: CompletionAssignment[] }>(
        '/services/reports/completion/assignments',
        { ...params, groupBy, key: groupKey }
    )
    const items = data?.items ?? []

    if (error)
        return (
            <div className={cx(styles, 'drill')}>
                <Alert type="error" showIcon title={error} />
            </div>
        )

    const columns = [
        {
            title: 'Задача',
            dataIndex: 'id',
            key: 'id',
            width: 90,
            render: (id: number, row: CompletionAssignment) => (
                <button
                    type="button"
                    className={cx(styles, 'nameLink')}
                    onClick={() =>
                        OpenResourceCardTask({
                            resource: AssignmentResource,
                            primaryFieldValue: row.uuid,
                            mode: 'view',
                        })
                    }
                >
                    #{id}
                </button>
            ),
        },
        {
            title: 'Підтверджено',
            dataIndex: 'confirmedAt',
            key: 'confirmedAt',
            width: 140,
            render: (value: string | null) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—'),
        },
        ...(groupBy !== 'driver'
            ? [{ title: 'Водій', dataIndex: 'driverName', key: 'driverName', render: (v: string | null) => v ?? '—' }]
            : []),
        ...(groupBy !== 'vehicle'
            ? [{ title: 'ТЗ', dataIndex: 'vehicleLabel', key: 'vehicleLabel', render: (v: string | null) => v ?? '—' }]
            : []),
        {
            title: 'Ходок',
            dataIndex: 'trips',
            key: 'trips',
            width: 80,
            align: 'right' as const,
            render: (v: number | null) => <span className={cx(styles, 'num')}>{formatNumber(v)}</span>,
        },
        {
            title: 'Тонн',
            dataIndex: 'tonnes',
            key: 'tonnes',
            width: 90,
            align: 'right' as const,
            render: (v: number | null) => <span className={cx(styles, 'num')}>{formatNumber(v, 3)}</span>,
        },
        {
            title: 'Затримка',
            dataIndex: 'hasDelay',
            key: 'hasDelay',
            width: 90,
            render: (v: boolean) =>
                v ? (
                    <span className={cx(styles, 'delayed')}>Так</span>
                ) : (
                    <span className={cx(styles, 'faint')}>Ні</span>
                ),
        },
        {
            title: 'Оцінка',
            dataIndex: 'averageRating',
            key: 'averageRating',
            width: 90,
            render: (v: number | null) =>
                v === null ? (
                    <span className={cx(styles, 'faint')}>—</span>
                ) : (
                    <span className={cx(styles, 'rating')}>
                        <MdStar style={{ color: '#faad14' }} aria-hidden="true" />
                        {v.toFixed(1)}
                    </span>
                ),
        },
    ]

    return (
        <div className={cx(styles, 'drill')}>
            <p className={cx(styles, 'drillTitle')}>Задачі групи</p>
            <Table<CompletionAssignment>
                className="wl-table"
                size="small"
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={items}
                pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'Задач немає' }}
            />
            {items.length >= LIMIT && (
                <div className={cx(styles, 'drillNote')}>Показано перші {LIMIT} задач — звузьте фільтри.</div>
            )}
        </div>
    )
}
